import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'rebass/styled-components';
import { useTheme } from 'styled-components';
import { Linkedin } from 'lucide-react';

const STATE_STYLES = {
  disabled: {
    bg: 'neutral.20',
    borderColor: 'transparent',
    borderStyle: 'solid',
    iconColor: 'neutral.400',
  },
  active: {
    bg: 'purple.100',
    borderColor: 'transparent',
    borderStyle: 'solid',
    iconColor: 'purple.500',
  },
  paused: {
    bg: 'neutral.0',
    borderColor: 'purple.400',
    borderStyle: 'dashed',
    iconColor: 'purple.500',
  },
  warning: {
    bg: 'orange.50',
    borderColor: 'orange.600',
    borderStyle: 'solid',
    iconColor: 'orange.600',
  },
};

const GOOGLE_PATH =
  'M8.2743 7.38282V9.25711H12.7543C12.6172 10.3085 12.2669 11.0783 11.7332 11.6188C11.0777 12.2743 10.0572 12.9903 8.2743 12.9903C5.51601 12.9903 3.36001 10.7657 3.36001 8.00739C3.36001 5.24911 5.51601 3.02454 8.2743 3.02454C9.76001 3.02454 10.8497 3.61139 11.6497 4.36568L12.968 3.04739C11.8554 1.96568 10.3617 1.14282 8.2743 1.14282C4.49544 1.14282 1.3183 4.22111 1.3183 7.99997C1.3183 11.7788 4.49544 14.8571 8.2743 14.8571C10.316 14.8571 11.8554 14.1868 13.0589 12.9371C14.2932 11.7028 14.6817 9.95825 14.6817 8.55597C14.6817 8.12168 14.6514 7.71768 14.5829 7.38282H8.2743Z';

const OUTLOOK_PATH =
  'M7.93659 15C5.91178 15 4.24945 14.3704 2.94959 13.1108C1.65013 11.8513 1 10.2074 1 8.17977C1 6.0383 1.65973 4.30653 2.9792 2.98407C4.29866 1.66123 6.02621 1 8.16183 1C10.1798 1 11.8226 1.63307 13.0896 2.89882C14.3635 4.16495 15 5.83384 15 7.90587C15 10.0342 14.3403 11.7506 13.0212 13.0541C11.7081 14.3515 10.0134 15 7.93659 15ZM7.995 12.3269C9.09882 12.3269 9.9874 11.9519 10.6599 11.2028C11.3329 10.4532 11.6689 9.4108 11.6689 8.07561C11.6689 6.68371 11.3425 5.60083 10.6895 4.82618C10.0362 4.05153 9.16443 3.6642 8.07381 3.6642C6.94999 3.6642 6.04541 4.06387 5.35967 4.86398C4.67393 5.65715 4.33107 6.70918 4.33107 8.0189C4.33107 9.34792 4.67393 10.3996 5.35967 11.1742C6.04541 11.9427 6.92398 12.3269 7.995 12.3269Z';

const resolveColor = (colors, token) => {
  const [key, shade] = token.split('.');
  return colors[key]?.[shade] || token;
};

const IntegrationIcon = ({ type, state }) => {
  const { colors } = useTheme();
  const styles = STATE_STYLES[state] || STATE_STYLES.disabled;
  const iconColor = resolveColor(colors, styles.iconColor);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        borderRadius: '4px',
        bg: styles.bg,
        border: '1px',
        borderStyle: styles.borderStyle,
        borderColor: styles.borderColor,
        color: iconColor,
        lineHeight: 0,
      }}
    >
      {type === 'linkedin' && <Linkedin size={14} strokeWidth={2} aria-hidden="true" />}
      {type === 'google' && (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d={GOOGLE_PATH} fill="currentColor" />
        </svg>
      )}
      {type === 'outlook' && (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d={OUTLOOK_PATH} fill="currentColor" />
        </svg>
      )}
      {state === 'warning' && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            bg: 'orange.600',
            border: '1.5px solid',
            borderColor: 'neutral.0',
          }}
        />
      )}
    </Box>
  );
};

IntegrationIcon.propTypes = {
  type: PropTypes.oneOf(['linkedin', 'google', 'outlook']).isRequired,
  state: PropTypes.oneOf(['disabled', 'active', 'paused', 'warning']).isRequired,
};

export default IntegrationIcon;
